/* eslint-disable camelcase */
import React, { useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, makeStyles, Button, CircularProgress, Theme, useTheme } from '@material-ui/core';
import { Popup, TileLayer, Marker, MapContainer } from 'react-leaflet';
import L, { LatLngExpression } from 'leaflet';
import clsx from 'clsx';
import { RobotHealthCondition, AlertStatus, RCNumericMetricValueName, GPSPosition, RobotInfo, RCNumericMetricDataPoint, TimeSeriesCollectionRawData }
    from '@moderepo/mode-apis';
import { capitalizeAll, DEFAULT_TSDB_SMART_MODULE_ID, getRobotHealthConditionName, getRobotTimeSeriesCollectionId, ModeTheme }
    from '@moderepo/mode-app-components';
import { fetchRobotCloudProjectSettings, rcDataContext, selectRobotCloudProjectSettings } from '@moderepo/mode-data-state-robot-cloud';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { fetchTimeSeriesCollectionRawData, userAppDataContext, selectTimeSeriesCollectionRawData } from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import * as RCUtils from './RCUtils';
import { RCRouteHelper } from './RCRouteHelper';



const useStyle = makeStyles((theme: Theme) => {
    return {
        mapContainer: {
            padding  : 5,
            width    : '100%',
            height   : '100%',
            minHeight: 700,
        },

        map: {
            width : '100%',
            height: '100%',
        },



        marker: {

            '& .marker-pin': {
                // Draw shadow
                width       : 2,
                height      : 2,
                borderRadius: '50%',
                position    : 'absolute',
                left        : '50%',
                top         : '50%',
                margin      : '-1px 0 0 -1px',
                boxShadow   : '0 0 6px 10px rgba(0, 0, 0, 0.4)',

                // Drawer marker
                '&:before': {
                    content     : '""',
                    width       : 40,
                    height      : 40,
                    borderRadius: '50% 50% 50% 0',
                    border      : '2px solid #ffffff',
                    background  : '#2479d4',
                    position    : 'absolute',
                    transform   : 'rotate(-45deg)',
                    left        : '50%',
                    top         : '50%',
                    margin      : '-45px 0 0 -20px',
                },

                // Draw white circle
                '&:after': {
                    content     : '""',
                    width       : 26,
                    height      : 26,
                    margin      : '-37px 0 0 -12px',
                    background  : theme.palette.background.paper,
                    position    : 'absolute',
                    borderRadius: '50%',
                },
            },

            // Icon
            '& .icon': {
                position : 'absolute',
                width    : 28,
                fontSize : 18,
                right    : 0,
                top      : -8,
                left     : -13,
                textAlign: 'center',
            },

            [`&.condition-${RobotHealthCondition.NORMAL}`]: {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.success.main,
                    },
                },
            },

            [`&.condition-${RobotHealthCondition.MINOR}`]: {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.primary.main,
                    },
                },
            },

            [`&.condition-${RobotHealthCondition.WARN}`]: {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.warning.main,
                    },
                },
            },

            [`&.condition-${RobotHealthCondition.CRITICAL}`]: {
                '& .marker-pin': {
                    '&:before': {
                        background: theme.palette.error.main,
                    },
                },
            },

        },


        markerPopupContent: {
            minWidth           : 200,
            display            : 'grid',
            gridTemplateColumns: 'auto 1fr',

            '& .label': {
                textAlign  : 'right',
                fontWeight : 'bold',
                marginRight: theme.spacing(0.5),
            },
        },

        markerPopupContentStatus: {
            paddingBottom: theme.spacing(0.5),
            display      : 'flex',
            flexDirection: 'row',

            '& .value': {
                color     : theme.palette.primary.main,
                fontWeight: 'bold',
                flex      : 1,
                width     : '100%',
            },

            [`&.condition-${RobotHealthCondition.NORMAL}`]: {
                '& .value': {
                    color: theme.palette.success.main,
                },
            },

            [`&.condition-${RobotHealthCondition.MINOR}`]: {
                '& .value': {
                    color: theme.palette.primary.main,
                },
            },

            [`&.condition-${RobotHealthCondition.WARN}`]: {
                '& .value': {
                    color: theme.palette.warning.main,
                },
            },

            [`&.condition-${RobotHealthCondition.CRITICAL}`]: {
                '& .value': {
                    color: theme.palette.error.main,
                },
            },
        },

        markerPopupContentIssues: {
            display      : 'flex',
            flexDirection: 'row',
            '& .label'   : {
                fontWeight : 'bold',
                marginRight: theme.spacing(0.5),
            },
            '& .value': {
                flex : 1,
                width: '100%',
            },
        },

        markerPopupContentActions: {
            paddingTop: theme.spacing(1),
            textAlign : 'right',
        },

    };
});


const gpsValueNames = [RCNumericMetricValueName.LATITUDE, RCNumericMetricValueName.LONGITUDE, RCNumericMetricValueName.ALTITUDE];

interface RCRobotsMapContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly robots: readonly RobotInfo[] | undefined;
    readonly defaultZoom?: number;
    readonly userId?: number;                                                           // The id of the user, home the user is looking at
    readonly deviceId?: number;                                                         // The id of the device, home the user is looking at
}


export const RCRobotsMapContainer = (props: RCRobotsMapContainerProps) => {
    const navigate = useNavigate();
    const { state:dataState } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const rcProjectSettings = selectRobotCloudProjectSettings(rcDataState, props.projectId);
    const rcRouteHelper = RCRouteHelper.getInstance(navigate);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const theme = useTheme() as ModeTheme;


    // The start date of data we want to fetch. We will use the current date as the start date
    // We use useRef because we want to keep this value unchanged during the life of this component
    const dataStartDate = useRef<string>(new Date().toISOString());



    const { devicesLatestDataById, centerLocation } = useMemo(() => {
        if (props.robots) {

            // Check if al the data is ready
            const allCollectionData = props.robots.map((robot: RobotInfo): TimeSeriesCollectionRawData | undefined => {
                const collectionId = getRobotTimeSeriesCollectionId(robot.robotClass, robot.id);

                return selectTimeSeriesCollectionRawData(
                    appDataState, robot.homeId, rcProjectSettings?.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID, collectionId,
                    dataStartDate.current, -1, gpsValueNames, [],
                );
            });

            // If none of the collection data is undefined then we can continue. If there is 1 collection data returned undefined then one of
            // the collection data is probably not done fetching so we will return and try again later
            if (!allCollectionData.includes(undefined)) {

                const result: {[robotId: string]: RCNumericMetricDataPoint | undefined} = {};

                // We want to focus the map at a location where the user can see some device. So we will pick the FIRST device which has a location
                let robotLocation: GPSPosition | undefined;

                for (let i = 0; i < props.robots.length; i += 1) {
                    const robot = props.robots[i];

                    const collectionData = allCollectionData[i];

                    // If there is at least 1 data points, use the last data point
                    if (collectionData && collectionData.data.length > 0) {
                        // Convert TS collection data into RobotCloudDataPoints array because it is easier to use.
                        const robotMetricData = RCUtils.collectionDataToRobotMetricDataPoint(collectionData, gpsValueNames);

                        // Use the last data point as the current robot metric values
                        const latestData = robotMetricData[robotMetricData.length - 1];
                        result[robot.id] = latestData;
                        if (latestData.values[RCNumericMetricValueName.LATITUDE] && latestData.values[RCNumericMetricValueName.LONGITUDE]
                                && !robotLocation) {
                            // If we haven't found a robot location yet, use this as the robotLocation
                            robotLocation = {
                                latitude : latestData.values[RCNumericMetricValueName.LATITUDE] || 0,
                                longitude: latestData.values[RCNumericMetricValueName.LONGITUDE] || 0,
                                altitude : 0,
                            };
                        }
                    }
                }


                return {
                    devicesLatestDataById: result,
                    // If we have robotLocation, use that location as the center of the map
                    centerLocation       : robotLocation || {
                        latitude: 0, longitude: 0, altitude: 0,
                    },
                };
            }
        }

        return {
            devicesLatestDataById: undefined,
            centerLocation       : undefined,
        };
    }, [props.robots, appDataState, rcProjectSettings]);



    /**
     * This useEffect is used for loading robot cloud project settings if it is not loaded
     */
    useEffect(() => {
        if (!rcProjectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [rcProjectSettings, props.projectId, rcDataDispatch]);



    /**
     * This useEffect is used for loading each device's latest TSDB data just to get the device's current GPS location
     */
    useEffect(() => {
        if (rcProjectSettings && props.robots && props.robots.length > 0) {
            props.robots.forEach((robot: RobotInfo) => {
                const collectionId = getRobotTimeSeriesCollectionId(robot.robotClass, robot.id);

                appDataDispatch(fetchTimeSeriesCollectionRawData(
                    robot.homeId, rcProjectSettings.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                    collectionId, dataStartDate.current, -1, gpsValueNames, [],
                ), true, true).catch(() => {
                    // ignore error because dataDispatch already handled the error
                });
            });
        }
    }, [props.robots, rcProjectSettings, dataStartDate, appDataDispatch]);



    const onRobotHealthSelected = useCallback((robotId: string, homeId: number) => {
        rcRouteHelper.gotoRobotPage({
            projectId: props.projectId,
            robotId,
            homeId,
            userId   : props.userId,
            deviceId : props.deviceId,
        });
    }, [rcRouteHelper, props.projectId, props.userId, props.deviceId]);


    // Use useMemo to create the mapCenter object so that we can cache it and not keep passing a new object to the MapContainer every re-render
    const mapCenter = useMemo((): LatLngExpression | undefined => {
        if (centerLocation) {
            return [centerLocation.latitude, centerLocation.longitude];
        }
        return undefined;
    }, [centerLocation]);


    return (
        <Paper className={clsx(props.className, classes.mapContainer)}>
            {props.robots && mapCenter && (
                <MapContainer className={classes.map} center={mapCenter} zoom={props.defaultZoom || 13}>
                    <TileLayer
                        attribution='<a href="https://www.maptiler.com/copyright/" target="_blank">&copy; MapTiler</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">&copy; OpenStreetMap contributors</a>'
                        url={`${theme.mapTilesURL}?key=0FuuQf9TW9Dp1F7i0rsp`}
                    />
                    {props.robots && (
                        props.robots.map((robot: RobotInfo) => {

                            // Aggregate the robot.status.alertStatuses and use the most severity alertStatus as the Robot's health condition.
                            const robotHealth = robot.status?.alertStatuses ? robot.status.alertStatuses.reduce((
                                aggregatedHealth: RobotHealthCondition, alertStatus: AlertStatus,
                            ): RobotHealthCondition => {
                                if (alertStatus.severity > aggregatedHealth) {
                                    // The alert status has higher severity level, use it as the device's health
                                    return alertStatus.severity as unknown as RobotHealthCondition;
                                }
                                return aggregatedHealth;
                            }, RobotHealthCondition.NORMAL) : undefined;

                            const deviceMetricData = devicesLatestDataById ? devicesLatestDataById[robot.id] : undefined;

                            const location: LatLngExpression = [
                                deviceMetricData?.values[RCNumericMetricValueName.LATITUDE] || 0,
                                deviceMetricData?.values[RCNumericMetricValueName.LONGITUDE] || 0,
                            ];

                            const markerIcon = L.divIcon({
                                className  : clsx(classes.marker, `condition-${robotHealth}`),
                                html       : '<div class="marker-pin"></div><i class="icon mode-icon robot-solid"/>',
                                iconSize   : [2, 54],
                                iconAnchor : [0, 30],
                                popupAnchor: [1, -42],
                            });

                            return (
                                <Marker
                                    key={`${robot.homeId}-${robot.id}`}
                                    position={location}
                                    icon={markerIcon}
                                >
                                    <Popup>
                                        <div className={classes.markerPopupContent}>
                                            <span className="label">{capitalizeAll(trans('label.robot'))}:</span>
                                            <div className={clsx(classes.markerPopupContentStatus)}>
                                                <span className="value">{robot.id}</span>
                                            </div>
                                            <span className="label">{capitalizeAll(trans('label.device_condition'))}:</span>
                                            <div className={clsx(classes.markerPopupContentStatus, `condition-${robotHealth}`)}>
                                                <span className="value">
                                                    {trans(
                                                        robotHealth !== undefined
                                                            ? getRobotHealthConditionName(robotHealth)
                                                            : 'label.unknown',
                                                    ).toUpperCase()}
                                                </span>
                                            </div>
                                            <span className="label">{capitalizeAll(trans('label.alert', { count: 2 }))}:</span>
                                            <div className={classes.markerPopupContentIssues}>
                                                <span className="value">
                                                    {
                                                        robot?.status?.alertStatuses?.length
                                                            ? robot.status.alertStatuses.map((alertStatus: AlertStatus) => {
                                                                return (
                                                                    <div key={`${alertStatus.id}`} className="alert">
                                                                        {alertStatus.name}
                                                                    </div>
                                                                );
                                                            })
                                                            : capitalizeAll(trans('label.none'))
                                                    }
                                                </span>
                                            </div>
                                            <span />
                                            <div className={classes.markerPopupContentActions}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    size="small"
                                                    onClick={() => {
                                                        onRobotHealthSelected(robot.id, robot.homeId);
                                                    }}
                                                >
                                                    {capitalizeAll(trans('label.view_robot_details'))}
                                                </Button>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                        })
                    )}
                </MapContainer>
            )}

            {!props.robots && (
                <CircularProgress />
            )}
        </Paper>
    );
};
